import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExitToApp from '@mui/icons-material/ExitToApp';
import School from '@mui/icons-material/School';
import Dashboard from '@mui/icons-material/Dashboard';
import Chat from '@mui/icons-material/Chat';
import MenuIcon from '@mui/icons-material/Menu';
import Notifications from '@mui/icons-material/Notifications';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Link, useLocation } from 'react-router-dom';
import { refreshSession } from '../apiBase';
import { useSessionTimers } from './useSessionTimers';
import SuperadminSchoolSwitcher from '../components/SuperadminSchoolSwitcher';

const AdminLayout = ({ user, onLogout, children, title = 'Yönetici Paneli' , onOpenMobileNav }) => {
  const theme = useTheme();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const menuOpen = Boolean(anchorEl);
  const schoolLabel = user?.school_id || user?.school_name || '';
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const session = useSessionTimers();
  const [dismissedUntil, setDismissedUntil] = React.useState(null);
  const nowSec = Math.floor(Date.now() / 1000);
  const showSessionDialog = !!session.showWarning && (!dismissedUntil || nowSec >= dismissedUntil);
  // Removed unused secondsLeft

  const NavLinkButton = ({ to, icon: Icon, label }) => {
    if (to === '/admin/whatsapp' && user?.role !== 'admin') return null;
    const isActive = location.pathname === to;
    return (
      <Button
        color="inherit"
        component={Link}
        to={to}
        startIcon={Icon ? <Icon /> : undefined}
        sx={{
          display: { xs: 'none', md: 'inline-flex' },
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          px: 1.5,
          ...(isActive
            ? { backgroundColor: alpha(theme.palette.common.white, 0.2) }
            : {
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.1) },
              }),
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{
        minHeight: isMobile ? 56 : undefined
      }}>
        <Toolbar sx={{ minHeight: isMobile ? 56 : undefined, px: isMobile ? 1 : 2 }}>
          {user && (
            <Button
              onClick={onOpenMobileNav}
              color="inherit"
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                minWidth: 0,
                mr: 1,
                px: 1,
                backgroundColor: alpha(theme.palette.common.white, 0.1),
                '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.2) },
                borderRadius: 2,
              }}
              startIcon={<MenuIcon />}
            />
          )}
          <School color="primary" sx={{ mr: isMobile ? 1 : 2, fontSize: isMobile ? 24 : 28 }} />
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {title}
          </Typography>

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Top navigation */}
              <NavLinkButton to="/admin" icon={Dashboard} label="Panel" />
              <NavLinkButton to="/admin/whatsapp" icon={Chat} label="WhatsApp Kayıt" />

              {/* Superadmin school switcher (only for superadmin users) */}
              {user?.role === 'superadmin' && !isMobile ? <SuperadminSchoolSwitcher /> : null}

              {/* Session countdown chip removed per request */}

              {/* User identity */}
              <Box sx={{ textAlign: 'right', ml: 1 }}>
                <Typography variant={isMobile ? 'caption' : 'body1'} sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {user.name} {user.surname}
                </Typography>
                {!isMobile && (
                  <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.2 }}>
                  {schoolLabel} • {user.role}
                  </Typography>
                )}
              </Box>

              {/* Logout */}
              {/* Logout button removed; users can logout via avatar menu */}
              {/* Notifications */}
              <IconButton color="inherit" sx={{ ml: 1 }}>
                <Badge color="error" variant="dot">
                  <Notifications />
                </Badge>
              </IconButton>

              {/* Profile dropdown */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 1 }} aria-controls={menuOpen ? 'profile-menu' : undefined} aria-haspopup="true" aria-expanded={menuOpen ? 'true' : undefined}>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {user?.name?.[0] || 'U'}
                  </Avatar>
                </IconButton>
                <Menu id="profile-menu" anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={handleMenuClose} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                  <MenuItem disabled>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {user.name} {user.surname}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {schoolLabel} • {user.role}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={onLogout}>
                    <ExitToApp fontSize="small" style={{ marginRight: 8 }} /> Çıkış Yap
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          )}
        </Toolbar>
        {user?.role === 'superadmin' && isMobile && (
          <Box sx={{ px: 2, pb: 1, minWidth: 0 }}>
            <SuperadminSchoolSwitcher fullWidth />
          </Box>
        )}
      </AppBar>

      {/* Main Content */}
      <Container
        maxWidth="xl"
        sx={{
          flex: 1,
          py: isMobile ? 2 : 3,
          px: isMobile ? 2 : 3,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Page Content */}
        <Box sx={{ flex: 1 }}>
          {children}
        </Box>
      </Container>

      {/* Footer */}
      <Paper
        component="footer"
        elevation={0}
        sx={{
          mt: 'auto',
          py: 3,
          backgroundColor: theme.palette.background.paper,
          color: 'text.secondary',
          borderRadius: 0,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Kolay Karekod
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Yapay Zeka Destekli Öğretmen-Veli Gelişim Platformu
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                © {new Date().getFullYear()} Tüm hakları saklıdır
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: alpha(theme.palette.common.white, 0.2) }} />

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 3
          }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Destek: info@kolaykarekod.com
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Tel: (0533) 396 1428
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Acil Durum: (0533) 396 1428
            </Typography>
          </Box>
        </Container>
      </Paper>

      {/* Session Expiry Dialog */}
       <Dialog open={showSessionDialog} onClose={() => { /* intentionally block closing while warning */ }} aria-labelledby="session-title">
         <DialogTitle id="session-title">İnaktiflik Nedeniyle Oturum Sona Erecek</DialogTitle>
         <DialogContent>
           <Typography variant="body2">
             Uzun süre işlem yapılmadı. Devam etmek için "Oturumu Sürdür" seçeneğini tıklayın.
           </Typography>
         </DialogContent>
         <DialogActions>
           <Button onClick={onLogout} color="secondary">Çıkış Yap</Button>
           <Button onClick={async () => {
             // Optimistically hide for at least 5 seconds to avoid flicker while network resolves
             setDismissedUntil(Math.floor(Date.now() / 1000) + 5);
             const ok = await refreshSession();
             if (!ok) {
               // If failed, allow dialog to reappear next tick
               setDismissedUntil(null);
             }
           }} variant="contained">Oturumu Sürdür</Button>
         </DialogActions>
       </Dialog>
    </Box>
  );
};

export default AdminLayout;
